import React, { useState, useEffect } from 'react';
import {
 TrophyIcon,
 UsersIcon,
 ChartBarIcon,
 CalendarIcon,
 CheckCircleIcon,
 StarIcon,
 FireIcon,
 ClockIcon,
 ArrowPathIcon
} from '@heroicons/react/24/outline';
import API_BASE_URL from '../Config/config';

const API = API_BASE_URL;

const TeamLeaderboardPage = () => {
 const [leaderboard, setLeaderboard] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [lastUpdated, setLastUpdated] = useState(null);

 useEffect(() => {
 fetchLeaderboard();
 // Auto-refresh every 30 seconds
 const interval = setInterval(fetchLeaderboard, 30000);
 return () => clearInterval(interval);
 }, []);

 const fetchLeaderboard = async () => {
 try {
 setLoading(true);
 const response = await fetch(`${API}/api/teams/leaderboard`);
 const data = await response.json();
 
 if (data.success) {
 setLeaderboard(data.leaderboard);
 setLastUpdated(new Date());
 } else {
 setError('Failed to fetch leaderboard');
 }
 } catch (err) {
 setError('Error loading leaderboard');
 console.error('Leaderboard fetch error:', err);
 } finally {
 setLoading(false);
 }
 };

 const getRankIcon = (rank) => {
 switch (rank) {
 case 1:
 return <TrophyIcon className="h-8 w-8 text-secondary" />;
 case 2:
 return <StarIcon className="h-8 w-8 text-muted" />;
 case 3:
 return <StarIcon className="h-8 w-8 text-accent" />;
 default:
 return (
 <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-600 text-white font-bold">
 {rank}
 </div>
 );
 }
 };

 const getRankBadgeColor = (rank) => {
 switch (rank) {
 case 1:
 return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-lg shadow-yellow-500/25';
 case 2:
 return 'bg-gradient-to-r from-gray-300 to-gray-500 text-white shadow-lg shadow-gray-400/25';
 case 3:
 return 'bg-gradient-to-r from-amber-500 to-amber-700 text-white shadow-lg shadow-amber-500/25';
 default:
 return 'bg-gray-700 text-muted';
 }
 };

 const getTopTeams = () => leaderboard.slice(0, 3);
 const getOtherTeams = () => leaderboard.slice(3);

 if (loading && leaderboard.length === 0) {
 return (
 <div className="min-h-screen bg-bgSection flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-muted">Loading leaderboard...</p>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-bgSection p-6">
 <div className="max-w-6xl mx-auto">
 {/* Header */}
 <div className="text-center mb-8">
 <div className="flex items-center justify-center gap-3 mb-4">
 <TrophyIcon className="h-12 w-12 text-secondary" />
 <h1 className="text-4xl font-bold text-white">Team Leaderboard</h1>
 </div>
 <p className="text-muted text-lg">
 Compete with other teams and climb to the top!
 </p>
           {lastUpdated && lastUpdated instanceof Date && !isNaN(lastUpdated) && (
             <p className="text-gray-500 text-sm mt-2">
               Last updated: {lastUpdated.toLocaleTimeString()}
             </p>
           )}
 </div>

 {/* Refresh Button */}
 <div className="flex justify-end mb-6">
 <button
 onClick={fetchLeaderboard}
 disabled={loading}
 className="flex items-center gap-2 px-4 py-2 bg-secondary text-black rounded-lg hover:bg-primary transition-colors disabled:opacity-50"
 >
 <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
 Refresh
 </button>
 </div>

 {error && (
 <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-6">
 <div className="flex items-center gap-2 text-red-400">
 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 {error}
 </div>
 </div>
 )}

 {/* Top 3 Teams - Podium Style */}
 {getTopTeams().length > 0 && (
 <div className="mb-12">
 <h2 className="text-2xl font-bold text-white mb-6 text-center">🏆 Top Performers</h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {getTopTeams().map((team, index) => {
 const rank = index + 1;
 return (
 <div
 key={team.team_id}
 className={`relative p-6 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
 rank === 1 
 ? 'border-primary bg-gradient-to-b from-yellow-900/30 to-yellow-800/20 order-2 md:order-2' 
 : rank === 2
 ? 'border-gray-400 bg-gradient-to-b from-gray-800/30 to-gray-700/20 order-1 md:order-1'
 : 'border-accent bg-gradient-to-b from-amber-900/30 to-amber-800/20 order-3 md:order-3'
 }`}
 >
 {/* Rank Badge */}
 <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
 <div className={`px-4 py-1 rounded-full text-sm font-bold ${getRankBadgeColor(rank)}`}>
 #{rank}
 </div>
 </div>

 {/* Team Info */}
 <div className="text-center mb-4">
 <div className="flex justify-center mb-3">
 {getRankIcon(rank)}
 </div>
 <h3 className="text-xl font-bold text-white mb-1">{team.team_name}</h3>
 <p className="text-muted text-sm">{team.role} • {team.level}</p>
 </div>

 {/* Score */}
 <div className="text-center mb-4">
 <div className="text-3xl font-bold text-white mb-1">{team.team_score}</div>
 <div className="text-sm text-muted">points</div>
 </div>

 {/* Stats */}
 <div className="space-y-2 text-sm">
 <div className="flex justify-between items-center">
 <span className="text-muted">Members:</span>
 <span className="text-white flex items-center gap-1">
 <UsersIcon className="h-4 w-4" />
 {team.member_count}
 </span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-muted">Progress:</span>
 <span className="text-white flex items-center gap-1">
 <ChartBarIcon className="h-4 w-4" />
 {team.completion_percentage}%
 </span>
 </div>
 <div className="flex justify-between items-center">
 <span className="text-muted">Phases:</span>
 <span className="text-white flex items-center gap-1">
 <CheckCircleIcon className="h-4 w-4" />
 {team.completed_phases}/{team.total_phases}
 </span>
 </div>
 </div>

 {/* Progress Bar */}
 <div className="mt-4">
 <div className="w-full bg-gray-700 rounded-full h-2">
 <div 
 className={`h-2 rounded-full transition-all duration-500 ${
 rank === 1 
 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' 
 : rank === 2
 ? 'bg-gradient-to-r from-gray-300 to-gray-500'
 : 'bg-gradient-to-r from-amber-500 to-amber-700'
 }`}
 style={{ width: `${team.completion_percentage}%` }}
 ></div>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}

 {/* Other Teams */}
 {getOtherTeams().length > 0 && (
 <div>
 <h2 className="text-2xl font-bold text-white mb-6">All Teams</h2>
 <div className="bg-bgSection rounded-lg overflow-hidden">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-gray-700">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Rank
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Team
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Role & Level
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Members
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Progress
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
 Score
 </th>
 </tr>
 </thead>
 <tbody className="bg-bgSection divide-y divide-gray-700">
 {getOtherTeams().map((team) => (
 <tr key={team.team_id} className="hover:bg-gray-700 transition-colors">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center">
 <span className="text-lg font-bold text-muted">#{team.rank}</span>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div>
 <div className="text-sm font-medium text-white">{team.team_name}</div>
 <div className="text-sm text-muted">
 {new Date(team.created_at).toLocaleDateString()}
 </div>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-sm text-muted">{team.role}</div>
 <div className="text-sm text-muted">{team.level}</div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center gap-1 text-sm text-muted">
 <UsersIcon className="h-4 w-4" />
 {team.member_count}
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center gap-2">
 <div className="w-16 bg-gray-700 rounded-full h-2">
 <div 
 className="bg-blue-500 h-2 rounded-full transition-all duration-500"
 style={{ width: `${team.completion_percentage}%` }}
 ></div>
 </div>
 <span className="text-sm text-muted">{team.completion_percentage}%</span>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-lg font-bold text-white">{team.team_score}</div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 </div>
 )}

 {leaderboard.length === 0 && !loading && (
 <div className="text-center py-12">
 <TrophyIcon className="h-24 w-24 mx-auto mb-6 text-gray-600" />
 <h3 className="text-xl font-semibold text-muted mb-2">No teams yet</h3>
 <p className="text-gray-500">Teams will appear here once they start working on projects</p>
 </div>
 )}
 </div>
 </div>
 );
};

export default TeamLeaderboardPage;



